import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export type EntityType = 'tool' | 'resource' | 'job_board';

export interface Rating {
  id: string;
  user_id: string;
  entity_type: EntityType;
  entity_id: string;
  rating: number;
  created_at: string;
  updated_at: string;
}

export interface RatingAggregate {
  entity_type: EntityType;
  entity_id: string;
  average_rating: number;
  total_ratings: number;
  distribution_1: number;
  distribution_2: number;
  distribution_3: number;
  distribution_4: number;
  distribution_5: number;
  updated_at: string;
}

export function useRatings(entityType: EntityType, entityId: string) {
  const [userRating, setUserRating] = useState<Rating | null>(null);
  const [aggregate, setAggregate] = useState<RatingAggregate | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchRatings = async () => {
    try {
      setLoading(true);

      // Fetch aggregate data
      const { data: aggregateData } = await supabase
        .from('rating_aggregates')
        .select('*')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .maybeSingle();

      setAggregate(aggregateData);

      // Fetch user's rating if authenticated
      if (user) {
        const { data: userRatingData } = await supabase
          .from('ratings')
          .select('*')
          .eq('entity_type', entityType)
          .eq('entity_id', entityId)
          .eq('user_id', user.id)
          .maybeSingle();

        setUserRating(userRatingData);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load ratings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async (rating: number) => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to rate items',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('ratings')
        .upsert({
          user_id: user.id,
          entity_type: entityType,
          entity_id: entityId,
          rating,
        })
        .select()
        .single();

      if (error) throw error;

      setUserRating(data);

      // Optimistic update
      if (aggregate) {
        const newTotal = userRating ? aggregate.total_ratings : aggregate.total_ratings + 1;
        const oldRating = userRating?.rating || 0;
        const oldSum = aggregate.average_rating * aggregate.total_ratings;
        const newSum = oldSum - oldRating + rating;
        const newAverage = newSum / newTotal;

        setAggregate({
          ...aggregate,
          average_rating: newAverage,
          total_ratings: newTotal,
        });
      } else {
        setAggregate({
          entity_type: entityType,
          entity_id: entityId,
          average_rating: rating,
          total_ratings: 1,
          distribution_1: rating === 1 ? 1 : 0,
          distribution_2: rating === 2 ? 1 : 0,
          distribution_3: rating === 3 ? 1 : 0,
          distribution_4: rating === 4 ? 1 : 0,
          distribution_5: rating === 5 ? 1 : 0,
          updated_at: new Date().toISOString(),
        });
      }

      toast({
        title: 'Rating Submitted',
        description: `You rated this ${entityType} ${rating} star${rating !== 1 ? 's' : ''}`,
      });
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit rating',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchRatings();
  }, [entityType, entityId, user?.id]);

  return {
    userRating,
    aggregate,
    loading,
    submitRating,
    refresh: fetchRatings,
  };
}